/**
 * OpenAppPage — fallback page for `/app/open`.
 *
 * Flow:
 *  - iOS with the TourismIntel app installed: the OS intercepts /app/open
 *    via AASA Universal Link BEFORE this page even loads. The native app
 *    opens directly. This page is never seen.
 *  - iOS without the app installed: the page loads. We auto-redirect to
 *    the App Store after 1.2s (long enough to show context, short enough
 *    that the user doesn't think the page is broken).
 *  - Android with the app installed: assetlinks.json + intent filter
 *    catches /app/open and opens the native app.
 *  - Android without app or desktop: auto-redirect to App Store (iOS link
 *    works on Android too — Google indexes it as a generic "get the app"
 *    landing) or shows a manual CTA.
 *
 * We intentionally don't navigate cross-store automatically on Android
 * (the user said only iOS App Store URL was confirmed); we surface both
 * manual options.
 */
import React, { useEffect, useState } from "react";
import { Smartphone, ArrowRight } from "lucide-react";

const APP_STORE_URL = "https://apps.apple.com/it/app/tourismintel/id6778354345";

export default function OpenAppPage() {
  const [autoRedirecting, setAutoRedirecting] = useState(true);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIOS = /iPhone|iPod|iPad/.test(ua);

    // Auto-redirect only iOS visitors (likely intended targets). Desktop
    // visitors might have landed here by accident — let them choose.
    if (!isIOS) {
      setAutoRedirecting(false);
      return;
    }
    const t = setTimeout(() => {
      window.location.replace(APP_STORE_URL);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="open-app-page"
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{ background: "#f6f7f8" }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-7 text-center"
        style={{
          background: "#ffffff",
          boxShadow: "0 0 0 1px rgba(186,201,204,0.18), 0 8px 32px rgba(25,28,29,0.06)",
        }}
      >
        <div
          className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #006874 0%, #0a8a99 100%)" }}
        >
          <Smartphone className="w-7 h-7 text-white" />
        </div>
        <h1
          className="text-2xl text-[#191c1d] mb-2"
          style={{ fontFamily: "'Reckless Neue Variable', serif", fontWeight: 500, letterSpacing: "-0.01em" }}
        >
          Open TourismIntel
        </h1>
        <p className="text-sm text-[#6b7a7d] leading-relaxed mb-6">
          {autoRedirecting
            ? "Taking you to the App Store… If the app is already installed, it should open automatically."
            : "Get the app to read briefings offline, receive push notifications and listen to the daily podcasts."}
        </p>
        <a
          href={APP_STORE_URL}
          data-testid="open-app-appstore-cta"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full text-sm font-semibold text-white transition-colors"
          style={{ background: "#006874" }}
        >
          Get it on the App Store
          <ArrowRight className="w-4 h-4" />
        </a>
        <a
          href="/"
          data-testid="open-app-back-home"
          className="block mt-3 text-xs text-[#6b7a7d] hover:text-[#006874] underline-offset-2 hover:underline"
        >
          Continue on the website
        </a>
      </div>
    </div>
  );
}
